import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useFeatures } from '@/lib/useFeatures';
import { Send, BookOpen, FileText, Film, Sparkles, AlertTriangle, RefreshCw, Quote } from 'lucide-react';

const SUGGESTED = [
  'How do I stop overthinking?',
  'What does HEADSPACE say about fear?',
  'How do I rewrite the stories I tell myself?',
  'What is the difference between thoughts and facts?',
  'How do I take back control of my mind?',
];

const sourceMeta = (type) => {
  if (type === 'lesson') return { Icon: BookOpen, label: 'LESSON' };
  if (type === 'video') return { Icon: Film, label: 'REAL TALK' };
  return { Icon: FileText, label: 'PAGE' };
};

export default function Oracle() {
  const { isOn, loading: flagsLoading } = useFeatures();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [revealedChars, setRevealedChars] = useState(0);
  const orbRef = useRef(null);
  const animRef = useRef(null);

  // Typewriter reveal on answer.
  useEffect(() => {
    if (!result?.answer) { setRevealedChars(0); return; }
    setRevealedChars(0);
    let i = 0;
    const total = result.answer.length;
    const step = Math.max(1, Math.floor(total / 220));
    const tick = () => {
      i = Math.min(total, i + step);
      setRevealedChars(i);
      if (i < total) animRef.current = window.setTimeout(tick, 18);
    };
    tick();
    return () => { if (animRef.current) window.clearTimeout(animRef.current); };
  }, [result]);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post('/oracle/ask', { question: text });
      setResult(data);
      // Scroll to answer area.
      setTimeout(() => {
        document.getElementById('oracle-answer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setError("The Oracle is currently sleeping. Ask the admin to enable this feature.");
      } else {
        setError(e?.response?.data?.detail || 'Something went wrong. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); ask(); };

  if (flagsLoading) {
    return <div className="kc-container py-16 text-muted-kc" data-testid="oracle-loading">Awakening the Oracle…</div>;
  }
  if (!isOn('oracle')) {
    return (
      <div className="kc-container py-16" data-testid="oracle-disabled">
        <div className="text-[11px] tracking-[0.18em] uppercase text-gold">The Oracle</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Currently resting.</h1>
        <p className="mt-3 text-muted-kc max-w-prose">The Oracle is not active right now. Check back soon.</p>
      </div>
    );
  }

  return (
    <div className="bg-ink text-white min-h-[calc(100vh-5rem)]" data-testid="oracle-page">
      <div className="kc-container py-12 md:py-20">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[11px] tracking-[0.18em] uppercase text-gold">The Oracle</div>
          <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] mt-3">Ask the book<br/>anything.</h1>
          <p className="mt-5 text-white/70 text-base md:text-lg">Trained on HEADSPACE, the Real Talk archive, and every page Mickey has written. Every answer cites its source.</p>
        </div>

        {/* Orb + Input */}
        <form onSubmit={onSubmit} className="mt-12 md:mt-16 max-w-3xl mx-auto" data-testid="oracle-form">
          <div className="flex flex-col items-center">
            <div
              ref={orbRef}
              className={`relative w-40 h-40 md:w-52 md:h-52 mb-8 ${loading ? 'oracle-orb-active' : 'oracle-orb-idle'}`}
              aria-hidden="true"
            >
              <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(212,164,55,0.85),rgba(212,164,55,0.15)_55%,rgba(0,0,0,0)_75%)] blur-[2px]"/>
              <div className="absolute inset-2 rounded-full border border-[color:var(--kc-gold)]/60"/>
              <div className="absolute inset-6 rounded-full border border-[color:var(--kc-gold)]/30"/>
              <div className="absolute inset-10 rounded-full border border-[color:var(--kc-gold)]/15"/>
              <div className="absolute inset-0 flex items-center justify-center text-[color:var(--kc-gold)]">
                <Sparkles size={28} className={loading ? 'animate-pulse' : ''} />
              </div>
            </div>
          </div>

          <div className="flex items-stretch gap-2 bg-white/5 border border-white/15 rounded-[var(--kc-radius-md)] focus-within:border-[color:var(--kc-gold)] transition-colors p-1.5">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="What do you want to ask the book?"
              rows={2}
              className="flex-1 bg-transparent text-white placeholder:text-white/40 px-3 py-2 outline-none resize-none text-base"
              data-testid="oracle-input"
              maxLength={1500}
            />
            <button
              type="submit"
              disabled={loading || !question.trim()}
              className="h-12 px-5 self-end rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold inline-flex items-center gap-2 hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="oracle-submit"
            >
              {loading ? <><RefreshCw size={16} className="animate-spin"/> Consulting</> : <>Ask <Send size={16}/></>}
            </button>
          </div>

          {/* Suggested prompts */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="oracle-suggestions">
            {SUGGESTED.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => { setQuestion(s); ask(s); }}
                disabled={loading}
                className="text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-[color:var(--kc-gold)] hover:text-[color:var(--kc-gold)] transition-colors disabled:opacity-40"
                data-testid="oracle-suggestion"
              >
                {s}
              </button>
            ))}
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="mt-10 max-w-3xl mx-auto bg-red-500/10 border border-red-500/40 text-red-200 rounded-[var(--kc-radius-md)] p-4 text-sm inline-flex items-start gap-2" data-testid="oracle-error">
            <AlertTriangle size={16} className="mt-0.5"/> {error}
          </div>
        )}

        {/* Answer */}
        {result && (
          <div id="oracle-answer" className="mt-12 md:mt-16 max-w-3xl mx-auto" data-testid="oracle-result">
            {result.crisis ? (
              <div className="bg-red-500/10 border border-red-400/40 rounded-[var(--kc-radius-md)] p-6" data-testid="oracle-crisis">
                <div className="inline-flex items-center gap-2 text-red-300 text-[11px] tracking-[0.18em] uppercase">
                  <AlertTriangle size={14}/> The Oracle steps aside.
                </div>
                <div className="mt-3 whitespace-pre-line text-white text-base md:text-lg leading-relaxed">
                  {result.answer}
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a href="tel:988" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="oracle-call-988">Call 988</a>
                  <a href="sms:988" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-white/20 font-semibold hover:bg-white/10 transition-colors" data-testid="oracle-text-988">Text 988</a>
                </div>
              </div>
            ) : (
              <article>
                <div className="text-[11px] tracking-[0.18em] uppercase text-gold inline-flex items-center gap-2">
                  <Quote size={14}/> The Oracle speaks
                </div>
                <div className="mt-4 text-white text-lg md:text-xl leading-[1.7] whitespace-pre-line font-reader" data-testid="oracle-answer-text">
                  {result.answer ? result.answer.slice(0, revealedChars) : ''}
                  {revealedChars < (result.answer?.length || 0) && (
                    <span className="inline-block w-2 h-5 align-middle bg-[color:var(--kc-gold)] animate-pulse ml-0.5"/>
                  )}
                </div>

                {/* Citations */}
                {(result.citations || []).length > 0 && (
                  <div className="mt-10" data-testid="oracle-citations">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-white/50">Sources</div>
                    <div className="mt-3 grid md:grid-cols-2 gap-3">
                      {result.citations.map((c, i) => {
                        const { Icon, label } = sourceMeta(c.source_type);
                        const inner = (
                          <div className="h-full bg-white/5 border border-white/10 hover:border-[color:var(--kc-gold)]/60 hover:bg-white/8 transition-all rounded-[var(--kc-radius-md)] p-4" data-testid={`oracle-citation-${i}`}>
                            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[color:var(--kc-gold)]">
                              <Icon size={12}/> {label}
                            </div>
                            <div className="mt-2 font-display uppercase text-lg leading-tight">{c.title}</div>
                            {c.snippet && <div className="mt-2 text-sm text-white/65 italic border-l-2 border-[color:var(--kc-gold)]/50 pl-3">&ldquo;{c.snippet}&rdquo;</div>}
                          </div>
                        );
                        if (!c.link) return <div key={i}>{inner}</div>;
                        const isExternal = /^https?:/i.test(c.link);
                        return isExternal ? (
                          <a key={i} href={c.link} target="_blank" rel="noreferrer">{inner}</a>
                        ) : (
                          <Link key={i} to={c.link}>{inner}</Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(!result.citations || result.citations.length === 0) && (
                  <div className="mt-6 text-sm text-white/50 italic">The Oracle could not find a direct source for this in the archive.</div>
                )}

                <div className="mt-10 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => { setResult(null); setQuestion(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="h-11 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors"
                    data-testid="oracle-ask-another"
                  >Ask another</button>
                  <Link to="/courses/headspace" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-white/20 font-semibold hover:bg-white/10 transition-colors" data-testid="oracle-read-book">Read the source</Link>
                </div>
              </article>
            )}
          </div>
        )}
      </div>

      {/* Orb animation styles */}
      <style>{`
        .oracle-orb-idle { animation: oracle-breathe 5s ease-in-out infinite; }
        .oracle-orb-active { animation: oracle-pulse 1.2s ease-in-out infinite; }
        @keyframes oracle-breathe {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.04); filter: brightness(1.15); }
        }
        @keyframes oracle-pulse {
          0%, 100% { transform: scale(1); filter: brightness(1) drop-shadow(0 0 18px rgba(212,164,55,0.45)); }
          50% { transform: scale(1.07); filter: brightness(1.3) drop-shadow(0 0 32px rgba(212,164,55,0.75)); }
        }
      `}</style>
    </div>
  );
}
